// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, Send, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface VideoOrder {
  id: string;
  purchaser_name: string;
  purchaser_email: string;
  purchaser_phone: string;
  player_name: string;
  team_name?: string;
  status: string;
  event_name?: string;
  package_type?: string;
  vendor_business_name?: string;
  vendor_phone?: string;
  vendor_email?: string;
  event?: {
    name: string;
    start_date: string;
  };
  package?: {
    package_type: string;
    price_cents: number;
    description: string;
  };
}

interface VideoMessage {
  id: string;
  order_id: string;
  sender_type: 'vendor' | 'customer';
  sender_name: string;
  message_text: string;
  created_at: string;
}

interface VideoMessageModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  order: VideoOrder | null;
  userType: 'vendor' | 'customer';
}

const VideoMessageModal: React.FC<VideoMessageModalProps> = ({
  open,
  onOpenChange,
  order,
  userType,
}) => {
  const { profile } = useAuth();
  const [messages, setMessages] = useState<VideoMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (open && order?.id) {
      loadMessages();
    }
  }, [open, order?.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadMessages = async () => {
    if (!order?.id) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('video_messages')
        .select('*')
        .eq('order_id', order.id)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error loading messages:', error);
        toast.error('Failed to load messages');
        return;
      }

      setMessages(data || []);
    } catch (error) {
      console.error('Error loading messages:', error);
      toast.error('Failed to load messages');
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !order?.id || !profile?.name) return;

    setIsSending(true);
    try {
      console.log('Sending message:', {
        order_id: order.id,
        sender_type: userType,
        sender_name: profile.name,
        message_text: newMessage.trim()
      });

      const { error } = await supabase
        .from('video_messages')
        .insert({
          order_id: order.id,
          sender_type: userType,
          sender_name: profile.name,
          message_text: newMessage.trim()
        });

      if (error) {
        console.error('Error sending message:', error);
        toast.error('Failed to send message');
        return;
      }

      // Send both email and SMS notifications
      const promises = [];

      // Email notification
      const emailData = {
        email_type: 'custom_notification',
        to_email: userType === 'vendor' ? order.purchaser_email : order.vendor_email,
        data: {
          name: userType === 'vendor' ? order.purchaser_name : 'Customer',
          subject: `New message about your video order - ${order.event_name || 'Event'}`,
          message: `
            <p>You have received a new message regarding your video order.</p>
            <div style="background-color: #f8f9fa; padding: 15px; border-radius: 8px; margin: 15px 0;">
              <p><strong>From:</strong> ${profile.name}</p>
              <p><strong>Player:</strong> ${order.player_name}</p>
              <p><strong>Event:</strong> ${order.event_name || 'N/A'}</p>
              <p><strong>Message:</strong></p>
              <p style="font-style: italic; background: white; padding: 10px; border-left: 3px solid #002E62;">"${newMessage.trim()}"</p>
            </div>
            <p>Please log in to your RecruitLook account to view all messages and respond.</p>
          `,
          event_name: order.event_name,
          player_name: order.player_name
        }
      };

      promises.push(
        invokeEdgeFunction('send-email', {
          body: emailData
        })
      );

      // SMS notification (only if phone number is available)
      console.log('🔍 SMS Debug - Detailed:', {
        userType: userType,
        userTypeType: typeof userType,
        purchaser_phone: order.purchaser_phone,
        vendor_phone: order.vendor_phone,
        vendorPhoneType: typeof order.vendor_phone,
        isCustomer: userType === 'customer',
        hasVendorPhone: !!order.vendor_phone,
        condition1: userType === 'vendor' && order.purchaser_phone,
        condition2: userType === 'customer' && order.vendor_phone,
        orderKeys: Object.keys(order)
      });

      if (userType === 'vendor' && order.purchaser_phone) {
        // Send SMS to customer when vendor replies
        const smsData = {
          to_phone: order.purchaser_phone,
          message: `New message from ${profile.name} regarding your video order for ${order.player_name}. Do not reply to this message - login to your dashboard here: https://recruitlook.com/parent/video-orders`
        };

        console.log('📱 Sending SMS to customer:', smsData);
        promises.push(
          invokeEdgeFunction('send-sms', {
            body: smsData
          })
        );
      } else if (userType === 'customer' && order.vendor_phone) {
        // Send SMS to vendor when customer replies
        const smsData = {
          to_phone: order.vendor_phone,
          message: `New message from ${profile.name} regarding video order for ${order.player_name}. Do not reply to this message - login to your dashboard here: https://recruitlook.com/vendor-video-dashboard/orders`
        };

        console.log('📱 Sending SMS to vendor:', smsData);
        promises.push(
          invokeEdgeFunction('send-sms', {
            body: smsData
          })
        );
      } else {
        console.log('❌ SMS not sent - missing phone or wrong user type');
      }

      // Send notifications
      try {
        await Promise.all(promises);
        console.log('Notifications sent successfully');
      } catch (notificationError) {
        console.error('Error sending notifications:', notificationError);
        // Don't fail the message send if notifications fail
      }

      setNewMessage('');
      toast.success('Message sent!');
      await loadMessages(); // Reload messages
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  console.log('VideoMessageModal render - order:', order, 'open:', open);
  
  if (!order) {
    console.log('VideoMessageModal: No order provided');
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="h-5 w-5" />
            Order Messages
          </DialogTitle>
          <DialogDescription>
            Message thread for order #{order.id.slice(-8)}
          </DialogDescription>
        </DialogHeader>

        {/* Order Summary */}
        <Card className="flex-shrink-0">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center justify-between">
              Order Details
              <Badge variant={order.status === 'completed' ? 'default' : 'secondary'}>
                {order.status.charAt(0).toUpperCase() + order.status.slice(1).replace('_', ' ')}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <h4 className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">Player Information</h4>
                <div>
                  <p className="font-medium">{order.player_name}</p>
                  {order.team_name && (
                    <p className="text-sm text-muted-foreground">{order.team_name}</p>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <h4 className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">Contact Information</h4>
                <div>
                  <p className="font-medium">{order.purchaser_name}</p>
                  <p className="text-sm text-muted-foreground">{order.purchaser_email}</p>
                  {order.purchaser_phone && (
                    <p className="text-sm text-muted-foreground">{order.purchaser_phone}</p>
                  )}
                </div>
              </div>
            </div>
            {(order.event_name || order.package_type) && (
              <div className="pt-2 border-t border-border/50 space-y-1">
                {order.event_name && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Event:</span>
                    <span className="font-medium">{order.event_name}</span>
                  </div>
                )}
                {order.package_type && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Package:</span>
                    <span className="font-medium capitalize">{order.package_type}</span>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto border rounded-lg p-4 space-y-4 min-h-[300px]">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No messages yet. Start the conversation!
            </div>
          ) : (
            messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${
                  message.sender_type === userType ? 'justify-end' : 'justify-start'
                }`}
              >
                <div
                  className={`max-w-[70%] rounded-lg p-3 ${
                    message.sender_type === userType
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium">
                      {message.sender_name}
                    </span>
                    <Badge 
                      variant="secondary" 
                      className="text-xs"
                    >
                      {message.sender_type}
                    </Badge>
                  </div>
                  <p className="text-sm">{message.message_text}</p>
                  <p className="text-xs opacity-70 mt-1">
                    {formatTimestamp(message.created_at)}
                  </p>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Message Input */}
        <div className="flex gap-2 pt-4 border-t">
          <Textarea
            placeholder="Type your message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            className="flex-1 min-h-[80px] resize-none"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
          />
          <Button
            onClick={sendMessage}
            disabled={!newMessage.trim() || isSending}
            className="self-end"
          >
            {isSending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VideoMessageModal;